<template>
    <div class="company-pennylane-sync">
        <!-- Message si la société ne peut pas être synchronisée -->
        <div v-if="!canBeSynced" class="alert alert-warning mb-0">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>{{ __('pennylane.sync.unavailable') }}</strong>
            <p class="mb-0 mt-2">{{ __('pennylane.sync.unavailable_message') }}</p>
        </div>

        <div v-else>
            <!-- Statut de synchronisation -->
            <div v-if="!company.pennylane_id && !company.pennylane_provider_id" class="alert alert-info mb-3">
                <i class="fas fa-info-circle mr-2"></i>
                <strong>{{ __('pennylane.sync.not_synced') }}</strong>
                <p class="mb-0 mt-1 small">{{ __('pennylane.sync.not_synced_message') }}</p>
            </div>

            <div v-else class="sync-status mb-3 p-3 bg-light rounded">
                <div v-if="company.pennylane_id" class="status-item">
                    <i class="fas fa-check-circle text-success mr-2"></i>
                    <span class="font-weight-bold">{{ __('pennylane.sync.customer_synced') }}</span>
                    <small class="d-block text-muted ml-4 mt-1">ID PennyLane : {{ company.pennylane_id }}</small>
                </div>
                <div v-if="company.pennylane_provider_id" class="status-item" :class="{'mt-3': company.pennylane_id}">
                    <i class="fas fa-check-circle text-success mr-2"></i>
                    <span class="font-weight-bold">{{ __('pennylane.sync.provider_synced') }}</span>
                    <small class="d-block text-muted ml-4 mt-1">ID PennyLane : {{ company.pennylane_provider_id }}</small>
                </div>
                <div v-if="company.pennylane_synced_at" class="status-item mt-2 pt-2 border-top">
                    <i class="fas fa-clock text-muted mr-2"></i>
                    <span class="text-muted">Dernière synchronisation : {{ formatSyncDate(company.pennylane_synced_at) }}</span>
                </div>
            </div>

            <!-- Avertissement de re-synchronisation nécessaire -->
            <div v-if="company.needs_pennylane_resync" class="alert alert-warning mb-3">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                <strong>Attention</strong>
                <p class="mb-0 mt-1 small">
                    <template v-if="company.pennylane_modified_fields && company.pennylane_modified_fields.length > 0">
                        Les données suivantes ont été modifiées depuis la dernière synchronisation :
                    </template>
                    <template v-else>
                        Les données de cette société ont été modifiées depuis la dernière synchronisation.
                    </template>
                </p>
                <ul v-if="company.pennylane_modified_fields && company.pennylane_modified_fields.length > 0" class="mb-0 mt-2 pl-3 small">
                    <li v-for="field in company.pennylane_modified_fields" :key="field">
                        <strong>{{ field }}</strong>
                    </li>
                </ul>
                <p v-if="company.pennylane_modified_fields && company.pennylane_modified_fields.length > 0" class="mb-0 mt-2 small">
                    Il est recommandé de mettre à jour les données dans PennyLane.
                </p>
            </div>

            <!-- Boutons d'action -->
            <div class="sync-actions d-flex mb-3" :class="{'justify-content-between': canSyncAsCustomer && canSyncAsProvider}">
                <button 
                    v-if="canSyncAsCustomer"
                    class="btn btn-primary"
                    :class="canSyncAsProvider ? 'flex-fill mr-2' : 'btn-block'"
                    @click="syncAsCustomer"
                    :disabled="syncing"
                >
                    <i class="fas" :class="syncing && syncType === 'customer' ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                    {{ company.pennylane_id ? __('pennylane.buttons.update_customer') : __('pennylane.buttons.sync_customer') }}
                </button>

                <button 
                    v-if="canSyncAsProvider"
                    class="btn btn-outline-primary"
                    :class="canSyncAsCustomer ? 'flex-fill' : 'btn-block'"
                    @click="syncAsProvider"
                    :disabled="syncing"
                >
                    <i class="fas" :class="syncing && syncType === 'provider' ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                    {{ company.pennylane_provider_id ? __('pennylane.buttons.update_provider') : __('pennylane.buttons.sync_provider') }}
                </button>
            </div>

            <!-- Configuration de la synchronisation -->
            <div class="sync-config mb-3">
                <h6 class="font-weight-bold mb-3">
                    <i class="fas fa-cog mr-2"></i>Configuration de la synchronisation
                </h6>
                
                <!-- Sélection de l'adresse -->
                <div class="form-group">
                    <label for="address-select">
                        Adresse à synchroniser 
                        <span v-if="canSyncAsCustomer" class="text-danger">*</span>
                        <span v-else class="text-muted">(optionnel)</span>
                    </label>
                    <select 
                        id="address-select"
                        v-model="selectedAddressId" 
                        class="form-control"
                        :class="{'is-invalid': !selectedAddressId && validationAttempted && canSyncAsCustomer}"
                    >
                        <option :value="null">-- Sélectionner une adresse --</option>
                        <option 
                            v-for="address in company.addresses" 
                            :key="address.id" 
                            :value="address.id"
                        >
                            {{ address.address_1 }}{{ address.address_2 ? ', ' + address.address_2 : '' }}, {{ address.zip_code }} {{ address.city }}
                            <template v-if="address.is_default"> (Par défaut)</template>
                        </option>
                    </select>
                    <small v-if="company.addresses && company.addresses.length === 0" class="form-text text-muted">
                        Aucune adresse disponible. Veuillez ajouter une adresse à la société.
                    </small>
                    <small v-else-if="!canSyncAsCustomer" class="form-text text-muted">
                        Si non renseignée, l'adresse par défaut sera utilisée.
                    </small>
                </div>
                
                <!-- Sélection du contact (pour particuliers - obligatoire) -->
                <div v-if="company.is_enterprise == 0 && canSyncAsCustomer" class="form-group">
                    <label for="contact-select">Contact à synchroniser <span class="text-danger">*</span></label>
                    <select 
                        id="contact-select"
                        v-model="selectedContactId" 
                        class="form-control"
                        :class="{'is-invalid': !selectedContactId && validationAttempted}"
                    >
                        <option :value="null">-- Sélectionner un contact --</option>
                        <option 
                            v-for="contact in company.contacts" 
                            :key="contact.id" 
                            :value="contact.id"
                        >
                            {{ contact.firstname }} {{ contact.lastname }}
                            <template v-if="contact.email"> ({{ contact.email }})</template>
                        </option>
                    </select>
                    <small class="form-text text-muted">
                        Le contact doit avoir un nom et un prénom pour les clients particuliers.
                    </small>
                </div>
                
                <!-- Sélection du contact (pour entreprises - optionnel) -->
                <div v-if="company.is_enterprise == 1 && canSyncAsCustomer" class="form-group">
                    <label for="contact-select-company">Contact principal (optionnel)</label>
                    <select 
                        id="contact-select-company"
                        v-model="selectedContactId" 
                        class="form-control"
                    >
                        <option :value="null">-- Aucun contact --</option>
                        <option 
                            v-for="contact in company.contacts" 
                            :key="contact.id" 
                            :value="contact.id"
                        >
                            {{ contact.firstname }} {{ contact.lastname }}
                            <template v-if="contact.phone"> ({{ contact.phone }})</template>
                        </option>
                    </select>
                    <small class="form-text text-muted">
                        Si renseigné, le téléphone et l'email du contact seront synchronisés.
                    </small>
                </div>
            </div>

            <!-- Informations de validation -->
            <div v-if="validationWarnings.length > 0" class="mb-3">
                <div class="alert alert-warning mb-0">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    <strong>{{ __('pennylane.validation.title') }}</strong>
                    <ul class="mb-0 mt-2 pl-3">
                        <li v-for="(warning, index) in validationWarnings" :key="index">{{ warning }}</li>
                    </ul>
                </div>
            </div>

            <!-- Résultat de la synchronisation -->
            <div v-if="result">
                <div v-if="result.success" class="alert alert-success mb-0">
                    <i class="fas fa-check-circle mr-2"></i>
                    {{ __('pennylane.success.message') }}
                </div>

                <div v-else class="alert alert-danger mb-0">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    <strong>{{ __('pennylane.errors.title') }}</strong>
                    <div class="mt-2">{{ result.error }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CompanyPennylaneSync',
    
    props: {
        companyId: {
            type: Number,
            required: true
        },
        initialCompany: {
            type: Object,
            default: () => ({})
        },
        // Client si l'envoi à Pennylane est activé, fournisseur si la réception l'est
        allowCustomer: {
            type: Boolean,
            default: true
        },
        allowProvider: {
            type: Boolean,
            default: true
        }
    },

    data() {
        // Sélectionner automatiquement l'adresse si une seule existe
        let defaultAddressId = this.initialCompany.selected_address_id || null;
        if (!defaultAddressId && this.initialCompany.addresses && this.initialCompany.addresses.length === 1) {
            defaultAddressId = this.initialCompany.addresses[0].id;
        }

        return {
            company: { ...this.initialCompany },
            syncing: false,
            syncType: null,
            result: null,
            selectedAddressId: defaultAddressId,
            selectedContactId: this.initialCompany.selected_contact_id || null,
            validationAttempted: false
        };
    },

    computed: {
        canBeSynced() {
            // Peut synchroniser si client OU fournisseur (entreprise ou particulier)
            return this.canSyncAsCustomer || this.canSyncAsProvider;
        },

        canSyncAsCustomer() {
            // Peut synchroniser si c'est un client (state == STATE_CLIENT = 3) ou si déjà synchronisé
            return this.allowCustomer && (this.company.state === 3 || !!this.company.pennylane_id);
        },

        canSyncAsProvider() {
            // Peut synchroniser si c'est un fournisseur ou si déjà synchronisé
            return this.allowProvider && (!!this.company.is_provider || !!this.company.pennylane_provider_id);
        },

        validationWarnings() {
            const warnings = [];
            
            // Vérifications pour client (entreprise ou particulier)
            if (this.canSyncAsCustomer) {
                // Vérifier qu'une adresse est sélectionnée
                if (!this.selectedAddressId) {
                    warnings.push('Vous devez sélectionner une adresse pour la synchronisation');
                }
                
                // Pour les particuliers, vérifier qu'un contact est sélectionné
                if (this.company.is_enterprise == 0 && !this.selectedContactId) {
                    warnings.push('Vous devez sélectionner un contact pour un client particulier');
                }
                
                // Vérifier que le contact sélectionné a bien nom et prénom (pour particuliers)
                if (this.company.is_enterprise == 0 && this.selectedContactId) {
                    const contact = this.company.contacts.find(c => c.id === this.selectedContactId);
                    if (!contact || !contact.firstname || !contact.lastname || 
                        contact.firstname.trim() === '' || contact.lastname.trim() === '') {
                        warnings.push('Le contact sélectionné doit avoir un nom et un prénom renseignés');
                    }
                }
                
                // Validations spécifiques aux entreprises clientes
                if (this.company.is_enterprise == 1) {
                    // IBAN : vérifier si l'IBAN existe mais est invalide
                    if (this.company.iban && this.company.has_valid_iban === false) {
                        warnings.push(this.__('pennylane.validation.warnings.iban_invalid'));
                    }
                    
                    // SIREN : vérifier le format uniquement s'il est renseigné
                    if (this.company.siren && this.company.siren.replace(/\s+/g, '').length !== 9) {
                        warnings.push(this.__('pennylane.validation.warnings.siren_invalid'));
                    }
                }
            }

            // Vérifications pour fournisseur (toujours entreprise)
            if (this.canSyncAsProvider) {
                // SIRET : vérifier le format uniquement s'il est renseigné
                if (this.company.siret && this.company.siret.replace(/\s+/g, '').length !== 14) {
                    warnings.push(this.__('pennylane.validation.warnings.siret_invalid'));
                }
                // SIREN : vérifier le format uniquement s'il est renseigné
                if (this.company.siren && this.company.siren.replace(/\s+/g, '').length !== 9) {
                    warnings.push(this.__('pennylane.validation.warnings.siren_invalid'));
                }
                // TVA : vérifier le format uniquement si renseigné (2 lettres + 2-13 chiffres)
                if (this.company.vat && !this.company.vat.replace(/\s+/g, '').match(/^[A-Z]{2}[0-9]{2,13}$/)) {
                    warnings.push(this.__('pennylane.validation.warnings.vat_format'));
                }
            }

            return warnings;
        },

        editCompanyUrl() {
            return `/crm/companies/${this.companyId}/edit`;
        }
    },

    methods: {
        async syncAsCustomer() {
            await this.sync('customer');
        },

        async syncAsProvider() {
            await this.sync('provider');
        },

        sync(type) {
            this.syncing = true;
            this.syncType = type;
            this.result = null;
            this.validationAttempted = true;

            fetch(`/api/companies/${this.companyId}/pennylane/sync`, {
                ...{
                    method: "POST"
                },
                ...this.getHttpJsonRequestHeaders(),
                body: JSON.stringify({
                    type: type,
                    address_id: this.selectedAddressId,
                    contact_id: this.selectedContactId
                })
            }).then((response) => {
                if (response.status == 200) {
                    response.json().then((data) => {
                        this.result = data;

                        if (data.success) {
                            // Mettre à jour les données locales
                            if (type === 'customer') {
                                this.company.pennylane_id = data.pennylane_id;
                            } else {
                                this.company.pennylane_provider_id = data.pennylane_id;
                            }

                          

                            // Recharger la page après 2 secondes pour mettre à jour toutes les données
                            setTimeout(() => {
                                window.location.reload();
                            }, 2000);
                        }
                    });
                } else {
                    response.json().then((data) => {
                        this.result = {
                            success: false,
                            error: data.error || 'Erreur lors de la synchronisation'
                        };

                       
                    });
                }
                this.syncing = false;
                this.syncType = null;
            }).catch((error) => {
                console.log(error);
                this.result = {
                    success: false,
                    error: error.message
                };

                this.$toasted.error(this.__('pennylane.errors.network'), {
                    duration: 5000
                });
                this.syncing = false;
                this.syncType = null;
            });
        },

        isFieldError(errorMessage) {
            // Vérifier si l'erreur concerne un champ manquant ou invalide
            const fieldErrorKeywords = ['obligatoire', 'requis', 'invalide', 'champ'];
            return fieldErrorKeywords.some(keyword => errorMessage.toLowerCase().includes(keyword));
        },

        formatSyncDate(dateString) {
            if (!dateString) return '';
            
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) {
                return 'à l\'instant';
            } else if (diffMins < 60) {
                return `il y a ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
            } else if (diffHours < 24) {
                return `il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
            } else if (diffDays < 7) {
                return `il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
            } else {
                return date.toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
        }
    }
};
</script>

<style scoped>
.company-pennylane-sync {
    padding: 0;
}

.sync-status {
    border-left: 3px solid #28a745;
}

.sync-status .status-item {
    padding: 4px 0;
}

.sync-actions .btn {
    font-size: 15px;
    padding: 12px 20px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.sync-actions .btn i {
    margin-right: 8px;
}

.sync-actions .btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.sync-actions .btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.alert {
    font-size: 14px;
    padding: 15px;
    border-radius: 6px;
}

.alert ul {
    padding-left: 20px;
    margin-top: 8px;
    font-size: 13px;
}

.alert li {
    margin-bottom: 6px;
    line-height: 1.6;
}

.alert strong {
    font-size: 15px;
}

.alert.alert-info {
    background-color: #e7f3ff;
    border-color: #b3d9ff;
    color: #004085;
}

.alert.alert-warning {
    background-color: #fff3cd;
    border-color: #ffc107;
    color: #856404;
}

.alert.alert-success {
    background-color: #d4edda;
    border-color: #28a745;
    color: #155724;
}

.alert.alert-danger {
    background-color: #f8d7da;
    border-color: #dc3545;
    color: #721c24;
}

.small {
    font-size: 13px;
}
</style>
